import type { ModuleId } from '@tahi/core';
import { useContext, useMemo } from 'react';
import { useStore } from '../ui/store';
import { computeUsable, type Usable } from './access';
import type { CommandEnv } from './commands/dispatch';
import { DataContext, type DataContextValue } from './context';
import type { MeResponse } from './me';
import { listModuleStates } from './repos/moduleStates';
import type { SyncStatus } from './syncClient';
import { useQuery } from './useQuery';

export function useData(): DataContextValue {
  const v = useContext(DataContext);
  if (!v) throw new Error('useData must be used inside DataProvider');
  return v;
}

export function useMe(): MeResponse | null {
  return useData().me;
}

export function useModuleStates() {
  return useQuery(['module_states'], (db) => listModuleStates(db));
}

export function useUsableModules(): Record<ModuleId, boolean> {
  const { me } = useData();
  const { data } = useModuleStates();
  return useMemo(() => computeUsable(me, data ?? []), [me, data]);
}

export function useUsable(): Usable {
  const usable = useUsableModules();
  return useMemo(() => (id: ModuleId) => usable[id] ?? false, [usable]);
}

export function useCommandEnv(): CommandEnv {
  const { db, userId, tz, sync } = useData();
  const usable = useUsable();
  return useMemo(() => ({ db, userId, tz, sync, usable }), [db, userId, tz, sync, usable]);
}

export function useSyncStatus(): SyncStatus {
  return useStore(useData().sync.status);
}
